/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useState } from "react";
import { Search, FilterX } from "lucide-react";
import { IMigration } from "../../../../store/api/migration/types";
import { constStr } from "../../../../globals/constants";
import { initialMigrationDataSelector } from "../../../../store/api/migration/selectors";
import { useSelector } from "react-redux";
import { Input } from "../../../ui/input";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Button } from "../../../ui/button";

interface ISiteMigrationFilterProps {
    setSiteMigrationData: (value: React.SetStateAction<IMigration[]>) => void;
}
export const SiteMigrationFilter: React.FC<ISiteMigrationFilterProps> = ({ setSiteMigrationData }) => {

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialMigrationData: IMigration[] = useSelector(initialMigrationDataSelector) || [];
    
    const [input, setInput] = useState("");
    const [connectionStatus, setConnectionStatus] = useState("");
    const [migrationStatus, setMigrationStatus] = useState("");

    useEffect(() => {
        let filteredData: IMigration[] = [...initialMigrationData];

        if (input.length >= 2) {
            filteredData = filteredData.filter((site: IMigration) => site.name.toLowerCase().includes(input.toLowerCase())
                || site.serialNumber.toLowerCase().includes(input.toLowerCase())
                || site.ipAddress.toLowerCase().includes(input.toLowerCase())
                || site?.migrationStatus?.toLowerCase().includes(input.toLowerCase())
            );
        }

        if (!!migrationStatus) {
            filteredData = filteredData.filter((site: IMigration) => site?.migrationStatus === migrationStatus);
        }

        if (!!connectionStatus) {
            filteredData = filteredData.filter((site: IMigration) => site?.connectionStatus === connectionStatus);
        }

        setSiteMigrationData(() => filteredData);
    }, [setSiteMigrationData, initialMigrationData, input, connectionStatus, migrationStatus]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const showReset = !!input || !!connectionStatus || !!migrationStatus;

    const handleReset = () => {
        setInput("");
        setConnectionStatus("");
        setMigrationStatus("");
    };

    return (
        <div className="flex items-center gap-2" style={{ height: 32, minHeight: 32 }}>
            <div className="relative shrink-0" style={{ width: 220 }}>
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    className="h-8 pl-8 text-sm"
                    placeholder="Search..."
                    value={input}
                    onChange={handleChange}
                />
            </div>
            <div className="shrink-0" style={{ width: 200 }}>
                <RadixSelectControl
                    value={connectionStatus || "__placeholder__"}
                    onValueChange={(val) => setConnectionStatus(val === "__placeholder__" ? "" : val)}
                    options={[
                        { value: "__placeholder__", label: "Connection Status" },
                        { value: "Connected", label: "Connected" },
                        { value: "Disconnected", label: "Disconnected" },
                    ]}
                    placeholder="Connection Status"
                />
            </div>
            <div className="shrink-0" style={{ width: 200 }}>
                <RadixSelectControl
                    value={migrationStatus || "__placeholder__"}
                    onValueChange={(val) => setMigrationStatus(val === "__placeholder__" ? "" : val)}
                    options={[
                        { value: "__placeholder__", label: "Migration Status" },
                        ...Object.keys(constStr.migrateStatus).map((key) => ({ value: key, label: key })),
                    ]}
                    placeholder="Migration Status"
                />
            </div>
            <Button
                variant="outline"
                size="icon"
                className="h-6 w-8 shrink-0"
                onClick={handleReset}
                aria-label="Reset All"
                style={{ visibility: showReset ? "visible" : "hidden" }}
            >
                <FilterX className="h-4 w-4" />
            </Button>
        </div>
    );
};

export default SiteMigrationFilter;